import { useSuspenseQuery } from '@apollo/client';
// eslint-disable-next-line lodash/import-scope
import { upperFirst } from 'lodash';
import React from 'react';

import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  Box,
  ColDefBuilder,
  DataTable,
  HStack,
  PageTitle,
  VStack,
} from '@eluve/components';
import { SiteBannerTypesEnum } from '@eluve/graphql-types';
import { getUserName } from '@eluve/utils';

import { CreateOrUpdateSiteBannerAction } from './CreateOrUpdateSiteBannerAction';
import { DeleteSiteBannerAction } from './DeleteSiteBannerAction';
import { SiteBannerStatusToggle } from './SiteBannerStatusToggle';
import { getSiteBannersAdminQuery, siteBannersFragment } from './operations';

type SiteBannerRow = {
  id: string;
  bannerType: SiteBannerTypesEnum;
  title: string;
  message: string;
  isActive: boolean | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  user: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
};

const cols = new ColDefBuilder<SiteBannerRow>()
  .defaultSortable('bannerType', {
    label: 'Banner Type',
    cellRenderer: (row) => upperFirst(row.bannerType.toLowerCase()),
  })
  .colDef({
    header: 'Title',
    cell: ({ row }) => row.original.title,
  })
  .colDef({
    header: 'Message',
    cell: ({ row }) => `${row.original.message.substring(0, 20)}...`,
  })
  .defaultSortable('createdBy', {
    label: 'Created By',
    cellRenderer: (row) =>
      getUserName({
        firstName: row.user.firstName,
        lastName: row.user.lastName,
        email: row.user.email,
      }),
  })
  .dateSortable('createdAt', 'Created At')
  .defaultSortable('isActive', {
    label: 'Enable',
    cellRenderer: (row) => <SiteBannerStatusToggle siteBannerId={row.id} />,
  })
  .colDef({
    header: 'Actions',
    cell: ({ row }) => (
      <Box hStack>
        <CreateOrUpdateSiteBannerAction
          siteBannerId={row.original.id}
          defaultValues={row.original}
        />
        <DeleteSiteBannerAction siteBannerId={row.original.id} />
      </Box>
    ),
  })
  .build();

export const SiteBannersListPage: React.FC = () => {
  useSuspenseQuery(getSiteBannersAdminQuery);

  const { siteBanners } = useCompleteFragment({
    fragment: siteBannersFragment,
    key: { id: QUERY_ROOT_ID },
  });

  return (
    <VStack>
      <HStack justify="between">
        <PageTitle>All Site Banners</PageTitle>
        <CreateOrUpdateSiteBannerAction />
      </HStack>
      <DataTable
        data={siteBanners as SiteBannerRow[]}
        columns={cols}
        enableGlobalSearch
        initialSortingState={[{ id: 'createdAt', desc: true }]}
      />
    </VStack>
  );
};
